import { create } from "zustand";
import { NavigateFunction } from "react-router-dom";
import { createApi } from "../../../utils/createApi";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { AxiosError } from "axios";
import {
  LoadingData,
  AllAlertsData,
  ParentGroupsAlertData,
  UserFilters,
  PercetageOfMotionData,
} from "../AnalyticsUtils/analyticsInterfaces";
import { getMax7Array } from "../AnalyticsUtils/getMax7Array";
import { getConversionArray } from "../AnalyticsUtils/getConversionArray";
import { Dispatcher } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";

interface AnalyticsState {
  // Loading states
  loadingData: LoadingData;
  pageLoading: boolean;
  loadingSites: boolean;
  loadingCameras: boolean;
  parentAccountsLoading: boolean;

  // Dropdown values and options
  siteDropDownValue: any[];
  cameraDropDownValue: any[];
  siteOptions: any[];
  cameraOptions: any[];

  // Data arrays and objects
  allAlertsData: AllAlertsData[];
  parentGroupsAlertsData: ParentGroupsAlertData[];
  falsePostiveData: any;
  percetageOfMotionData: any;
  visibleArray: any[];
  conversionArray: any[];
  parentAccounts: any[];
  parentIds: Map<any, any>;
  parentNames: Map<any, any>;
  totalAlerts: number;

  // UI state
  showMoreVisible: boolean;
  showMoreOpen: boolean;
  displayOnLeftBool: boolean;
  filtersSavedClicked: boolean;
  dateChangesMade: boolean;
  filtersApplied: boolean;

  // Names and URLs
  alertGraphName: string;
  dateGraphName: string;
  downloadURL: string;
  alertURL: string;

  // Filters and metrics
  userFilters: UserFilters;
  averagePercentageOfMotion: number;
  alertState: {
    dateTooFar: boolean;
    startAfterEnd: boolean;
    endBeforeStart: boolean;
  };

  // Chart dimensions
  barChartHeight: number;
  barChartHeightFull: number;
  outerBoxHeight: number;
  outerBoxHeightFull: number;
  innerBoxHeight: number;
  innerBoxHeightFull: number;

  // Chart dimension setters
  setBarChartHeight: (height: number) => void;
  setOuterBoxHeight: (height: number) => void;
  setInnerBoxHeight: (height: number) => void;

  // Setters
  setLoadingData: (loadingData: Partial<LoadingData>) => void;
  setPageLoading: (loading: boolean) => void;
  setLoadingSites: (loading: boolean) => void;
  setLoadingCameras: (loading: boolean) => void;
  setParentAccountsLoading: (loading: boolean) => void;
  setSiteDropDownValue: (value: any[]) => void;
  setCameraDropDownValue: (value: any[]) => void;
  setSiteOptions: (options: any[]) => void;
  setCameraOptions: (options: any[]) => void;
  setAllAlertsData: (data: AllAlertsData[]) => void;
  setParentGroupsAlertsData: (data: ParentGroupsAlertData[]) => void;
  setFalsePostiveData: (data: any) => void;
  setPercetageOfMotionData: (data: any) => void;
  setVisibleArray: (array: any[]) => void;
  setConversionArray: (array: any[]) => void;
  setParentAccounts: (accounts: any[]) => void;
  setParentIds: (ids: Map<any, any>) => void;
  setParentNames: (names: Map<any, any>) => void;
  setTotalAlerts: (total: number) => void;
  setShowMoreVisible: (visible: boolean) => void;
  setShowMoreOpen: (open: boolean) => void;
  setDisplayOnLeftBool: (display: boolean) => void;
  setFiltersSavedClicked: (clicked: boolean) => void;
  setDateChangesMade: (changed: boolean) => void;
  setFiltersApplied: (applied: boolean) => void;
  setAlertsGraphName: (name: string) => void;
  setDateGraphName: (name: string) => void;
  setDownloadURL: (url: string) => void;
  setAlertURL: (url: string) => void;
  setUserFilters: (
    filters: UserFilters | ((prev: UserFilters) => UserFilters)
  ) => void;
  setAveragePercentageOfMotion: (percentage: number) => void;
  setAlertState: (state: {
    dateTooFar: boolean;
    startAfterEnd: boolean;
    endBeforeStart: boolean;
  }) => void;

  // Functions
  fetchAllGraphsData: (navigate: NavigateFunction) => void;

  // Add resetState function
  resetState: () => void;
}

const startDate = new Date();
startDate.setDate(startDate.getDate() - 5);
const endDate = new Date();

const savedAnalyticsFilters = JSON.parse(
  localStorage.savedAnalyticsFilters || "null"
);
const savedSiteDropDownValue = JSON.parse(
  localStorage.siteDropDownValueAnalytics || "null"
);
const savedCameraDropDownValue = JSON.parse(
  localStorage.cameraDropDownValueAnalytics || "null"
);

const getTotalAlerts = (allAlertsData: AllAlertsData[]) => {
  let totalAlerts = 0;
  allAlertsData.forEach((alert) => {
    totalAlerts += alert.alert_volume;
  });
  return totalAlerts;
};

export const useAnalyticsStore = create<AnalyticsState>((set, get) => ({
  // Loading states
  loadingData: {
    allAlertsData: true,
    parentGroupsData: true,
    falsePositiveData: true,
    percetageOfMotionData: true,
  },
  pageLoading: false,
  loadingSites: false,
  loadingCameras: false,
  parentAccountsLoading: true,

  // Dropdown values and options
  siteDropDownValue:
    localStorage.filtersSavedClicked === "true" && savedSiteDropDownValue
      ? savedSiteDropDownValue
      : [],
  cameraDropDownValue:
    localStorage.filtersSavedClicked === "true" && savedCameraDropDownValue
      ? savedCameraDropDownValue
      : [],
  siteOptions: [],
  cameraOptions: [],

  // Data arrays and objects
  allAlertsData: [],
  parentGroupsAlertsData: [],
  falsePostiveData: [],
  percetageOfMotionData: [],
  visibleArray: [],
  conversionArray: [],
  parentAccounts: [],
  parentIds: new Map(),
  parentNames: new Map(),
  totalAlerts: 0,

  // UI state
  showMoreVisible: false,
  showMoreOpen: false,
  displayOnLeftBool: false,
  filtersSavedClicked: localStorage.filtersSavedClicked === "true",
  dateChangesMade: false,
  filtersApplied: false,

  // Names and URLs
  alertGraphName: "Parent Group",
  dateGraphName: "",
  downloadURL: "",
  alertURL: "",

  // Filters and metrics
  userFilters:
    localStorage.filtersSavedClicked === "true" && savedAnalyticsFilters
      ? {
          parent_group: savedAnalyticsFilters.parent_group,
          site: savedAnalyticsFilters.site,
          camera: savedAnalyticsFilters.camera,
          start_date: startDate,
          end_date: endDate,
        }
      : {
          parent_group: "",
          site: [],
          camera: [],
          start_date: startDate,
          end_date: endDate,
        },
  averagePercentageOfMotion: NaN,
  alertState: {
    dateTooFar: false,
    startAfterEnd: false,
    endBeforeStart: false,
  },

  // Chart dimensions
  barChartHeight: 385,
  barChartHeightFull: 600,
  outerBoxHeight: 425,
  outerBoxHeightFull: 640,
  innerBoxHeight: 423,
  innerBoxHeightFull: 638,

  // Chart dimension setters
  setBarChartHeight: (height) => set({ barChartHeight: height }),
  setOuterBoxHeight: (height) => set({ outerBoxHeight: height }),
  setInnerBoxHeight: (height) => set({ innerBoxHeight: height }),

  // Setters
  setLoadingData: (loadingData) =>
    set((state) => ({
      loadingData: { ...state.loadingData, ...loadingData },
    })),
  setPageLoading: (loading) => set({ pageLoading: loading }),
  setLoadingSites: (loading) => set({ loadingSites: loading }),
  setLoadingCameras: (loading) => set({ loadingCameras: loading }),
  setParentAccountsLoading: (loading) =>
    set({ parentAccountsLoading: loading }),
  setSiteDropDownValue: (value) => set({ siteDropDownValue: value }),
  setCameraDropDownValue: (value) => set({ cameraDropDownValue: value }),
  setSiteOptions: (options) => set({ siteOptions: options }),
  setCameraOptions: (options) => set({ cameraOptions: options }),
  setAllAlertsData: (data) => set({ allAlertsData: data }),
  setParentGroupsAlertsData: (data) => set({ parentGroupsAlertsData: data }),
  setFalsePostiveData: (data) => set({ falsePostiveData: data }),
  setPercetageOfMotionData: (data) => set({ percetageOfMotionData: data }),
  setVisibleArray: (array) => set({ visibleArray: array }),
  setConversionArray: (array) => set({ conversionArray: array }),
  setParentAccounts: (accounts) => set({ parentAccounts: accounts }),
  setParentIds: (ids) => set({ parentIds: ids }),
  setParentNames: (names) => set({ parentNames: names }),
  setTotalAlerts: (total) => set({ totalAlerts: total }),
  setShowMoreVisible: (visible) => set({ showMoreVisible: visible }),
  setShowMoreOpen: (open) => set({ showMoreOpen: open }),
  setDisplayOnLeftBool: (display) => set({ displayOnLeftBool: display }),
  setFiltersSavedClicked: (clicked) => set({ filtersSavedClicked: clicked }),
  setDateChangesMade: (changed) => set({ dateChangesMade: changed }),
  setFiltersApplied: (applied) => set({ filtersApplied: applied }),
  setAlertsGraphName: (name) => set({ alertGraphName: name }),
  setDateGraphName: (name) => set({ dateGraphName: name }),
  setDownloadURL: (url) => set({ downloadURL: url }),
  setAlertURL: (url) => set({ alertURL: url }),
  setUserFilters: (filters) =>
    set((state) => ({
      userFilters:
        typeof filters === "function" ? filters(state.userFilters) : filters,
    })),
  setAveragePercentageOfMotion: (percentage) =>
    set({ averagePercentageOfMotion: percentage }),
  setAlertState: (state) => set({ alertState: state }),

  // Reset state function
  resetState: () => {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 5);
    const endDate = new Date();

    const initialResetState = {
      loadingData: {
        allAlertsData: true,
        parentGroupsData: true,
        falsePositiveData: true,
        percetageOfMotionData: true,
      },
      pageLoading: false,
      loadingSites: false,
      loadingCameras: false,
      parentAccountsLoading: true,
      siteOptions: [],
      cameraOptions: [],
      allAlertsData: [],
      parentGroupsAlertsData: [],
      falsePostiveData: [],
      percetageOfMotionData: [],
      visibleArray: [],
      conversionArray: [],
      parentAccounts: [],
      parentIds: new Map(),
      parentNames: new Map(),
      totalAlerts: 0,
      showMoreVisible: false,
      showMoreOpen: false,
      displayOnLeftBool: false,
      dateChangesMade: false,
      // alertGraphName: "Parent Group",
      dateGraphName: "",
      downloadURL: "",
      alertURL: "",
      averagePercentageOfMotion: NaN,
      alertState: {
        dateTooFar: false,
        startAfterEnd: false,
        endBeforeStart: false,
      },
      barChartHeight: 385,
      barChartHeightFull: 600,
      outerBoxHeight: 425,
      outerBoxHeightFull: 640,
      innerBoxHeight: 423,
      innerBoxHeightFull: 638,
    };

    set(initialResetState);

    if (localStorage.filtersSavedClicked !== "true") {
      set({
        siteDropDownValue: [],
        cameraDropDownValue: [],
        filtersSavedClicked: false,
        alertGraphName: "Parent Group",
        userFilters: {
          parent_group: "",
          site: [],
          camera: [],
          start_date: startDate,
          end_date: endDate,
        },
      });
    }
  },

  // Functions
  fetchAllGraphsData: async (navigate: NavigateFunction) => {
    const store = get();

    const {
      parent_group: groupId,
      start_date,
      end_date,
      site: siteIdsArray,
    } = store.userFilters;

    // Convert dates to string format for API
    const startDate = start_date
      ? start_date.toISOString().split("T")[0]
      : undefined;
    const endDate = end_date ? end_date.toISOString().split("T")[0] : undefined;

    const searchParamsString = buildSearchParamsString(
      groupId,
      startDate,
      endDate,
      siteIdsArray
    );

    store.setLoadingData({
      allAlertsData: true,
      parentGroupsData: true,
      falsePositiveData: true,
      percetageOfMotionData: true,
    });

    // Fetch all data
    await Promise.all([
      fetchAlertVolumeData(navigate, searchParamsString),
      fetchParentGroupsAlertData(navigate, searchParamsString),
      fetchFalsePositiveDataAnalyticsPage(navigate, searchParamsString),
      fetchPercentageMotionFilteredData(navigate, searchParamsString),
    ]);

    store.setDateChangesMade(false);
  },
}));

const buildSearchParamsString = (
  groupId?: string,
  startDate?: string,
  endDate?: string,
  siteIdsArray: string[] = [],
  cameraIdsArray: string[] = []
): string => {
  let searchParamsString = "";

  // Handle date and group combinations
  if (!groupId && !endDate && startDate) {
    searchParamsString = `?start_date=${startDate}`;
  }
  if (!groupId && endDate && !startDate) {
    searchParamsString = `?end_date=${endDate}`;
  }
  if (groupId && !endDate && startDate) {
    searchParamsString = `?group=${groupId}&start_date=${startDate}`;
  }
  if (groupId && endDate && !startDate) {
    searchParamsString = `?group=${groupId}&end_date=${endDate}`;
  }
  if (groupId && endDate && startDate) {
    searchParamsString = `?group=${groupId}&start_date=${startDate}&end_date=${endDate}`;
  }
  if (!groupId && endDate && startDate) {
    searchParamsString = `?start_date=${startDate}&end_date=${endDate}`;
  }

  // Handle site IDs
  if (siteIdsArray[0]) {
    let additionalSiteString = `&site=${siteIdsArray[0]}`;
    if (siteIdsArray.length > 1) {
      siteIdsArray.forEach((siteId: string, index: number) => {
        if (index !== 0) {
          additionalSiteString += `,${siteId}`;
        }
      });
    }
    searchParamsString += additionalSiteString;
  }

  // Handle camera IDs - this overrides previous params if cameras are specified
  if (cameraIdsArray[0]) {
    let additionalCamerasString = `${cameraIdsArray[0]}`;
    if (cameraIdsArray.length > 1) {
      cameraIdsArray.forEach((cameraId: string, index: number) => {
        if (index !== 0) {
          additionalCamerasString += `,${cameraId}`;
        }
      });
    }
    searchParamsString = `?camera=${additionalCamerasString}&start_date=${startDate}&end_date=${endDate}`;
  }

  return searchParamsString;
};

export const fetchAlertVolumeData = async (
  navigate: NavigateFunction,
  searchParamsString: string
) => {
  const store = useAnalyticsStore.getState();
  const api = createApi("");

  try {
    const response = await api.get(
      `analytics/alert_volume/${searchParamsString}`
    );
    store.setAllAlertsData(response.data);
    store.setTotalAlerts(getTotalAlerts(response.data));
    store.setLoadingData({ allAlertsData: false });
  } catch (error) {
    const axiosError = error as AxiosError;
    universalAPIErrorHandler(
      axiosError,
      navigate,
      () => fetchAlertVolumeData(navigate, searchParamsString),
      () => {
        store.setLoadingData({ allAlertsData: false });
      }
    );
  }
};
export const fetchParentGroupsAlertData = async (
  navigate: NavigateFunction,
  searchParamsString: string
) => {
  const store = useAnalyticsStore.getState();
  const api = createApi("");

  try {
    const response = await api.get(
      `analytics/alerts_by_parent/${searchParamsString}`
    );
    const updatedData = response.data;

    // Process the data to add abbreviated names
    for (const key in updatedData) {
      const updatedKey = updatedData[key];
      if (updatedKey.name.length > 4) {
        updatedKey.abreviated_name = `${updatedKey.name.slice(0, 3)}...`;
      } else {
        updatedKey.abreviated_name = updatedKey.name;
      }
    }

    // Update store state
    store.setParentGroupsAlertsData(updatedData);

    const conversionArray = getConversionArray(
      updatedData,
      store.setConversionArray as any,
      store.setShowMoreVisible as any
    );

    store.setVisibleArray(getMax7Array(conversionArray));
    store.setShowMoreOpen(false);
    store.setLoadingData({ parentGroupsData: false });
  } catch (error) {
    const axiosError = error as AxiosError;
    universalAPIErrorHandler(
      axiosError,
      navigate,
      () => fetchParentGroupsAlertData(navigate, searchParamsString),
      () => {
        // Update graph name based on filters
        if (!store.userFilters.parent_group) {
          store.setAlertsGraphName("Parent Group");
        } else if (store.userFilters.parent_group) {
          store.setAlertsGraphName("Site");
        } else if (store.userFilters.site[0]) {
          store.setAlertsGraphName("Camera");
        }

        store.setLoadingData({ parentGroupsData: false });
      }
    );
  }
};

export const fetchFalsePositiveDataAnalyticsPage = async (
  navigate: NavigateFunction,
  searchParamsString: string
) => {
  const store = useAnalyticsStore.getState();
  const api = createApi("");

  try {
    const response = await api.get(
      `analytics/false_positive/${searchParamsString}`
    );
    store.setFalsePostiveData(response.data);
    store.setLoadingData({ falsePositiveData: false });
  } catch (error) {
    const axiosError = error as AxiosError;
    universalAPIErrorHandler(
      axiosError,
      navigate,
      () => fetchFalsePositiveDataAnalyticsPage(navigate, searchParamsString),
      () => {
        store.setLoadingData({ falsePositiveData: false });
      }
    );
  }
};

export const fetchPercentageMotionFilteredData = async (
  navigate: NavigateFunction,
  searchParamsString: string
) => {
  const store = useAnalyticsStore.getState();
  const api = createApi("");

  try {
    // Reset average percentage before fetching new data
    store.setAveragePercentageOfMotion(NaN);

    const response = await api.get(
      `analytics/percentage_motion_with_average/${searchParamsString}`
    );
    const newDataArray: PercetageOfMotionData[] = [];

    if (response.data.report) {
      const data = response.data.report;
      if (data[0]) {
        data.forEach((object: PercetageOfMotionData) => {
          const newObject = { ...object };
          const newPercentFiltered = object.percentage_filtered.toFixed(2);
          newObject.percentage_filtered = Number(newPercentFiltered);
          newDataArray.push(newObject);
        });
      }
    }

    store.setAveragePercentageOfMotion(response.data.average || 0);
    store.setPercetageOfMotionData(newDataArray);
    store.setLoadingData({ percetageOfMotionData: false });
  } catch (error) {
    const axiosError = error as AxiosError;
    universalAPIErrorHandler(
      axiosError,
      navigate,
      () => fetchPercentageMotionFilteredData(navigate, searchParamsString),
      () => {
        store.setLoadingData({ percetageOfMotionData: false });
      }
    );
  }
};

export const fetchCamerasBySite = async (
  idArray: string[],
  navigate: NavigateFunction
) => {
  const store = useAnalyticsStore.getState();
  const api = createApi("");

  store.setLoadingCameras(true);

  if (idArray.length === 0) {
    store.setCameraOptions([]);
    store.setLoadingCameras(false);
    return;
  }

  // Build the ID string for the API call
  const idString = idArray.reduce((acc, id, index) => {
    if (index === 0) return id;
    return `${acc},${id}`;
  }, "");

  try {
    const response = await api.get(`camera/site/?customers=${idString}`);
    store.setCameraOptions(response.data.results);
    store.setLoadingCameras(false);
  } catch (error) {
    const axiosError = error as AxiosError;
    universalAPIErrorHandler(
      axiosError,
      navigate,
      () => fetchCamerasBySite(idArray, navigate),
      () => {
        alert("Error retrieving cameras.");
        store.setLoadingCameras(false);
      }
    );
  }
};

export const fetchCamerasBySiteAndSetDropdownValues = async (
  idArray: string[],
  cameraIds: number[],
  navigate: NavigateFunction
) => {
  const store = useAnalyticsStore.getState();
  const api = createApi("");

  store.setLoadingCameras(true);

  if (idArray.length === 0) {
    store.setCameraOptions([]);
    store.setLoadingCameras(false);
    return;
  }

  // Build the ID string for the API call
  const idString = idArray.reduce((acc, id, index) => {
    if (index === 0) return id;
    return `${acc},${id}`;
  }, "");

  try {
    const response = await api.get(`camera/site/?customers=${idString}`);
    store.setCameraOptions(response.data.results);

    // Create maps for ID to name conversion
    const cameraNames = new Map();
    response.data.results.forEach((object: any) => {
      cameraNames.set(object.id, object.camera_name);
    });

    // Set dropdown values based on camera IDs from URL
    const cameraNamesArray = cameraIds
      .map((cameraId) => cameraNames.get(cameraId))
      .filter(Boolean);

    if (cameraNamesArray.length > 0) {
      store.setCameraDropDownValue(cameraNamesArray);
      localStorage.setItem(
        "cameraDropDownValueAnalytics",
        JSON.stringify(cameraNamesArray)
      );
    }

    store.setLoadingCameras(false);
  } catch (error) {
    const axiosError = error as AxiosError;
    universalAPIErrorHandler(
      axiosError,
      navigate,
      () =>
        fetchCamerasBySiteAndSetDropdownValues(idArray, cameraIds, navigate),
      () => {
        alert("Error retrieving cameras.");
        store.setLoadingCameras(false);
      }
    );
  }
};

export const fetchSitesByParentGroup = async (
  id: string,
  navigate: NavigateFunction
) => {
  const store = useAnalyticsStore.getState();
  const api = createApi("");

  store.setLoadingSites(true);

  try {
    const response = await api.get(`customer/names/${id}/`);
    store.setSiteOptions(response.data);
    store.setLoadingSites(false);
  } catch (error) {
    const axiosError = error as AxiosError;
    universalAPIErrorHandler(
      axiosError,
      navigate,
      () => fetchSitesByParentGroup(id, navigate),
      () => {
        alert("Error retrieving sites. Please try again");
        store.setLoadingSites(false);
      }
    );
  }
};

export const fetchSitesByParentGroupAndSetDropdownValues = async (
  id: string,
  siteIds: number[],
  navigate: NavigateFunction
) => {
  const store = useAnalyticsStore.getState();
  const api = createApi("");

  store.setLoadingSites(true);

  try {
    const response = await api.get(`customer/names/${id}/`);
    store.setSiteOptions(response.data);

    // Create maps for ID to name conversion
    const siteNames = new Map();
    response.data.forEach((object: any) => {
      siteNames.set(object.id, object.name);
    });

    // Set dropdown values based on site IDs from URL
    const siteNamesArray = siteIds
      .map((siteId) => siteNames.get(siteId))
      .filter(Boolean);

    if (siteNamesArray.length > 0) {
      store.setSiteDropDownValue(siteNamesArray);
      localStorage.setItem(
        "siteDropDownValueAnalytics",
        JSON.stringify(siteNamesArray)
      );
    }

    store.setLoadingSites(false);
  } catch (error) {
    const axiosError = error as AxiosError;
    universalAPIErrorHandler(
      axiosError,
      navigate,
      () => fetchSitesByParentGroupAndSetDropdownValues(id, siteIds, navigate),
      () => {
        alert("Error retrieving sites. Please try again");
        store.setLoadingSites(false);
      }
    );
  }
};

export const handleNavigatedFromSitesPage = async (
  navigate: NavigateFunction
) => {
  const store = useAnalyticsStore.getState();
  const api = createApi("");

  store.setPageLoading(true);

  const parentGroupId = localStorage.analyticsParentGroupId;
  const siteId = localStorage.analyticsSiteId;
  const idArray = [siteId];

  // Build the ID string for the API call
  const idString = idArray.reduce((acc, id, index) => {
    if (index === 0) return id;
    return `${acc},${id}`;
  }, "");

  try {
    // Fetch sites by parent group
    const sitesResponse = await api.get(
      `customer/by_group_id/${parentGroupId}/?page_size=1000`
    );
    store.setSiteOptions(sitesResponse.data.results);
    store.setLoadingSites(false);

    // Fetch cameras by site
    const camerasResponse = await api.get(`camera/site/?customers=${idString}`);
    store.setCameraOptions(camerasResponse.data.results);
    store.setPageLoading(false);

    // Update user filters with the site data
    store.setUserFilters((prev) => ({
      ...prev,
      parent_group: parentGroupId,
      site: [siteId],
    }));

    // Fetch all analytics data
    await fetchAllAnalyticsPageData(navigate);
  } catch (error) {
    const axiosError = error as AxiosError;
    universalAPIErrorHandler(
      axiosError,
      navigate,
      () => handleNavigatedFromSitesPage(navigate),
      () => {
        alert("Error retrieving sites. Please try again");
        store.setPageLoading(false);
        store.setLoadingSites(false);
      }
    );
  }

  localStorage.setItem("navigatedToAnalyticsFromSitesPage", "false");
};

export const handleShowMoreButtonClick = () => {
  const store = useAnalyticsStore.getState();

  if (!store.showMoreOpen) {
    store.setDisplayOnLeftBool(true);
    store.setShowMoreOpen(true);
    store.setVisibleArray(store.conversionArray);
    store.setBarChartHeight(store.barChartHeightFull);
    store.setOuterBoxHeight(store.outerBoxHeightFull);
    store.setInnerBoxHeight(store.innerBoxHeightFull);
  } else {
    store.setDisplayOnLeftBool(false);
    store.setShowMoreOpen(false);
    store.setVisibleArray(getMax7Array(store.conversionArray));
    store.setBarChartHeight(385);
    store.setOuterBoxHeight(425);
    store.setInnerBoxHeight(423);
  }
};

export const handleFilterCameras = () => {
  const store = useAnalyticsStore.getState();
  const cameraIdsArray = store.userFilters.camera;

  // Filter parent groups alerts data based on camera IDs
  const updatedParentGroupsAlertsData = store.parentGroupsAlertsData.filter(
    (object) => cameraIdsArray.includes(object.id.toString())
  );

  // Update store state
  store.setParentGroupsAlertsData(updatedParentGroupsAlertsData);

  // Get conversion array and update visible array
  const conversionArray = getConversionArray(
    updatedParentGroupsAlertsData,
    store.setConversionArray,
    store.setShowMoreVisible as unknown as Dispatcher<boolean>
  );

  store.setVisibleArray(getMax7Array(conversionArray));
  store.setShowMoreOpen(false);
  store.setLoadingData({ parentGroupsData: false });
};

export const handleGetDownloadURL = async (navigate: NavigateFunction) => {
  const store = useAnalyticsStore.getState();
  const api = createApi("");

  const {
    parent_group: groupId,
    start_date,
    end_date,
    site: siteIdsArray,
    camera: cameraIdsArray,
  } = store.userFilters;

  // Convert dates to string format for API
  const startDate = start_date
    ? start_date.toISOString().split("T")[0]
    : undefined;
  const endDate = end_date ? end_date.toISOString().split("T")[0] : undefined;

  // Build the base URL with required date parameters
  let url = `analytics/download/?start_date=${startDate}&end_date=${endDate}`;

  // Add optional parameters if they exist
  if (groupId) {
    url += `&group=${groupId}`;
  }

  if (siteIdsArray.length > 0) {
    const siteString = siteIdsArray.join(",");
    url += `&site=${siteString}`;
  }

  if (cameraIdsArray.length > 0) {
    const cameraString = cameraIdsArray.join(",");
    url += `&camera=${cameraString}`;
  }

  try {
    const response = await api.get(url);
    store.setDownloadURL(response.data.url);
  } catch (error) {
    const axiosError = error as AxiosError;
    universalAPIErrorHandler(
      axiosError,
      navigate,
      () => handleGetDownloadURL(navigate),
      () => {}
    );
  }
};

export const fetchAllAnalyticsPageData = async (navigate: NavigateFunction) => {
  const store = useAnalyticsStore.getState();
  const api = createApi("");

  try {
    // Reset loading states
    store.setLoadingData({
      allAlertsData: true,
      parentGroupsData: true,
      falsePositiveData: true,
      percetageOfMotionData: true,
    });

    // Fetch parent accounts
    const parentAccountsResponse = await api.get(
      "group/?parent_account=true&page_size=10000"
    );
    const parentAccounts = parentAccountsResponse.data.results;

    const parentIds = new Map();
    const parentNames = new Map();

    parentAccounts.forEach((object: any) => {
      parentIds.set(object.name, object.id);
      parentNames.set(object.id, object.name);
    });

    store.setParentAccounts(parentAccounts);
    store.setParentIds(parentIds);
    store.setParentNames(parentNames);
    store.setParentAccountsLoading(false);

    // Fetch all analytics data
    await store.fetchAllGraphsData(navigate);

    // Get download URL
    await handleGetDownloadURL(navigate);
  } catch (error) {
    universalAPIErrorHandler(
      error as AxiosError,
      navigate,
      () => fetchAllAnalyticsPageData(navigate),
      () => {
        store.setLoadingData({
          allAlertsData: false,
          parentGroupsData: false,
          falsePositiveData: false,
          percetageOfMotionData: false,
        });
        store.setParentAccountsLoading(false);
      }
    );
  }
};

export const fetchAllAlertsData = async (navigate: NavigateFunction) => {
  const store = useAnalyticsStore.getState();
  const api = createApi("");

  const {
    parent_group: groupId,
    start_date,
    end_date,
    site: siteIdsArray,
    camera: cameraIdsArray,
  } = store.userFilters;

  // Convert dates to string format for API
  const startDate = start_date
    ? start_date.toISOString().split("T")[0]
    : undefined;
  const endDate = end_date ? end_date.toISOString().split("T")[0] : undefined;

  let searchParamsString = ``;

  if (!groupId && !endDate && startDate) {
    searchParamsString = `?start_date=${startDate}`;
  }
  if (!groupId && endDate && !startDate) {
    searchParamsString = `?end_date=${endDate}`;
  }
  if (groupId && !endDate && startDate) {
    searchParamsString = `?group=${groupId}&start_date=${startDate}`;
  }
  if (groupId && endDate && !startDate) {
    searchParamsString = `?group=${groupId}&end_date=${endDate}`;
  }
  if (groupId && endDate && startDate) {
    searchParamsString = `?group=${groupId}&start_date=${startDate}&end_date=${endDate}`;
  }
  if (!groupId && endDate && startDate) {
    searchParamsString = `?start_date=${startDate}&end_date=${endDate}`;
  }
  if (siteIdsArray[0]) {
    let additionalSiteString = `&site=${siteIdsArray[0]}`;
    if (siteIdsArray.length > 1) {
      siteIdsArray.forEach((siteId: string, index: number) => {
        if (index !== 0) {
          additionalSiteString += `,${siteId}`;
        }
      });
    }
    searchParamsString += additionalSiteString;
  }
  if (cameraIdsArray[0]) {
    let additionalCamerasString = `${cameraIdsArray[0]}`;
    if (cameraIdsArray.length > 1) {
      cameraIdsArray.forEach((cameraId: string, index: number) => {
        if (index !== 0) {
          additionalCamerasString += `,${cameraId}`;
        }
      });
    }
    searchParamsString = `?camera=${additionalCamerasString}&start_date=${startDate}&end_date=${endDate}`;
  }

  try {
    store.setLoadingData({ allAlertsData: true });
    const response = await api.get(
      `analytics/alert_volume/${searchParamsString}`
    );
    store.setAllAlertsData(response.data);
    store.setLoadingData({ allAlertsData: false });
  } catch (error) {
    universalAPIErrorHandler(
      error as AxiosError,
      navigate,
      () => fetchAllAlertsData(navigate),
      () => {
        store.setLoadingData({ allAlertsData: false });
      }
    );
  }
};

export const handleSetDefaultAnalyticsFilters = (checkBoxValue: boolean) => {
  const store = useAnalyticsStore.getState();

  const setFiltersSavedClicked = store.setFiltersSavedClicked;

  if (checkBoxValue) {
    setFiltersSavedClicked(true);
    localStorage.setItem("filtersSavedClicked", "true");
    localStorage.setItem(
      "savedAnalyticsFilters",
      JSON.stringify(store.userFilters)
    );
  } else {
    setFiltersSavedClicked(false);
    localStorage.setItem("filtersSavedClicked", "false");
  }
};
